import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSuicideIntentAssessment } from "../../../store/slices/suicideIntentSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function BeckSuicideFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentSuicideIntentAssessment, isLoading, error } = useSelector(
        (state) => state.suicideIntent || {}
    );

    useEffect(() => {
        if (id) dispatch(fetchSuicideIntentAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentSuicideIntentAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentSuicideIntentAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading SIS Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentSuicideIntentAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentSuicideIntentAssessment;

    // Mapping based on your exact backend response structure
    const sisItems = [
        { title: "Isolation", scoreKey: "isolation_score", valueKey: "isolation_value" },
        { title: "Timing", scoreKey: "timing_score", valueKey: "timing_value" },
        { title: "Precautions against discovery", scoreKey: "precautions_score", valueKey: "precautions_value" },
        { title: "Acting to Get Help", scoreKey: "acting_to_get_help_score", valueKey: "acting_to_get_help_value" },
        { title: "Final Acts (Anticipation of Death)", scoreKey: "final_acts_score", valueKey: "final_acts_value" },
        { title: "Active Preparation for Attempt", scoreKey: "active_preparation_score", valueKey: "active_preparation_value" },
        { title: "Suicide Note", scoreKey: "suicide_note_score", valueKey: "suicide_note_value" },
        { title: "Overt Communication Before Attempt", scoreKey: "overt_communication_score", valueKey: "overt_communication_value" },
        { title: "Alleged Purpose of Attempt", scoreKey: "alleged_purpose_score", valueKey: "alleged_purpose_value" },
        { title: "Expectations of Fatality", scoreKey: "expectations_fatality_score", valueKey: "expectations_fatality_value" },
        { title: "Conception of Method's Lethality", scoreKey: "conception_lethality_score", valueKey: "conception_lethality_value" },
        { title: "Seriousness of Attempt", scoreKey: "seriousness_score", valueKey: "seriousness_value" },
        { title: "Attitude Toward Living/Dying", scoreKey: "attitude_living_dying_score", valueKey: "attitude_living_dying_value" },
        { title: "Conception of Medical Rescuability", scoreKey: "conception_rescuability_score", valueKey: "conception_rescuability_value" },
        { title: "Degree of Premeditation", scoreKey: "premeditation_score", valueKey: "premeditation_value" },
        { title: "Reaction to Attempt", scoreKey: "reaction_attempt_score", valueKey: "reaction_attempt_value" },
        { title: "Visualization of Death", scoreKey: "visualization_death_score", valueKey: "visualization_death_value" },
        { title: "Number of Previous Attempts", scoreKey: "previous_attempts_score", valueKey: "previous_attempts_value" },
        { title: "Alcohol Intake Relationship", scoreKey: "alcohol_intake_alcohol_score", valueKey: "alcohol_intake_alcohol_value" },
        { title: "Drug Intake Relationship", scoreKey: "intake_drug_score", valueKey: "intake_drug_value" }
    ];

    const totalScore = sisItems.reduce((sum, item) => sum + (parseInt(d[item.scoreKey] || 0, 10)), 0);

    const getRiskLevel = (score) => {
        if (score <= 10) return { text: "Low Risk", color: "#28a745", recommendation: "Send home with advice to see Community Mental Health Team or GP." };
        if (score <= 19) return { text: "Medium Risk", color: "#ffc107", recommendation: "Assessment by CMHT or Psychiatrist advisable. Follow-up required." };
        return { text: "High Risk", color: "#dc3545", recommendation: "Immediate psychiatric assessment. Admission recommended." };
    };

    const risk = getRiskLevel(totalScore);

    return (
        <>
            <style jsx global>{`
        * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: 'Segoe UI', Arial, sans-serif !important; }
        body { background: #fff !important; color: #000 !important; font-size: 11px !important; }
        .print-container { padding: 30px !important; position: relative !important; }
        .logo { position: absolute !important; top: 15px !important; left: 15px !important; }
        .logo img { height: 60px !important; width: auto !important; }
        .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
        .header h3 { font-size: 1.5rem !important; font-weight: bold !important; }
        .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 15px !important; }
        .meta-table td { border: 1px solid #000 !important; padding: 6px !important; }
        .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 18% !important; }
        .sis-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 100px !important; }
        .sis-table th, .sis-table td { border: 1px solid #000 !important; padding: 5px 8px !important; }
        .sis-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
        .score-col { width: 50px !important; text-align: center !important; font-weight: bold !important; font-size: 13px !important; }
        .risk-banner { margin-top: 20px !important; border: 2px solid #000 !important; padding: 15px !important; page-break-inside: avoid !important; }
        .risk-level { font-size: 1.3rem !important; font-weight: bold !important; text-transform: uppercase !important; }
        .footer { margin-top: 25px !important; border-top: 1px solid #000 !important; padding-top: 8px !important; text-align: center !important; font-size: 9px !important; }
        .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 40px !important; }
        .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
        @media print { .sis-table tr { page-break-inside: avoid !important; } }
      `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>BECK SUICIDE INTENT SCALE (SIS) REPORT</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Diagnosis</td>
                            <td>{d.diagnosis}</td>
                            <td className="label-bg">Assessed By</td>
                            <td>{d.clinician_name}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="sis-table">
                    <thead>
                        <tr>
                            <th style={{ width: '30px' }}>#</th>
                            <th style={{ width: '180px' }}>Scale Item</th>
                            <th>Patient Response / Observations</th>
                            <th className="score-col">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sisItems.map((item, index) => (
                            <tr key={index}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td style={{ fontWeight: 'bold' }}>{item.title}</td>
                                <td>{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* <div className="risk-banner">
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #000', paddingBottom: '10px' }}>
                        <div>
                            <small style={{ display: 'block' }}>CUMULATIVE SCORE</small>
                            <strong style={{ fontSize: '1.4rem' }}>Total: {totalScore}</strong>
                        </div>
                        <div className="risk-level" style={{ color: risk.color }}>{risk.text}</div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <strong>Clinical Recommendation:</strong> <span style={{ fontStyle: 'italic' }}>{risk.recommendation}</span>
                    </div>
                </div> */}

                <div style={{ marginTop: '15px' }}>
                    <strong>Additional Comments:</strong>
                    <div style={{ border: '1px solid #000', padding: '10px', minHeight: '40px', marginTop: '5px' }}>
                        {d.additional_comments || "No additional comments."}
                    </div>
                </div>

                <div className="sig-row">
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Clinician Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}